import React, { useMemo, useState, useEffect } from 'react';
import { startOfWeek, addDays, format, subDays } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import { utilisationApi } from '@/services/utilisationApi';
import type { UtilisationSummary, UtilisationMetrics, CapacitySummary, ProjectUtilisation, UtilisationTrend } from '@/types/utilisation';
import { 
  Users, 
  Clock, 
  CheckSquare, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Calendar,
  Award,
  Filter,
  ArrowUpDown,
  Bell,
  X,
  CheckCircle2,
  XCircle,
  Eye,
  MessageSquare,
  MoreVertical,
  Search,
  Download,
  FileText,
  BarChart3,
  Sparkles,
  PieChart,
  Activity,
  Zap,
  Settings,
  HelpCircle,
  Info,
  Loader2,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { PersonaBadgeToggle } from './PersonaBadgeToggle';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useManagerDashboardSummary } from '@/hooks/useManagerDashboardSummary';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';

interface ManagerDashboardProps {
  onNavigate?: (path: string) => void;
}

export const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ onNavigate }) => {
  const { timesheets, employees, projects, currentEmployee, updateTimesheet } = useTimesheetForScreen('dashboard');
  const { getScreenMode } = useDataSource();
  const { summary: managerSummary, mode } = useManagerDashboardSummary('dashboard');
  
  // Utilisation data for Key Metrics - uses same mode as utilisation report screen
  const { isLive: isUtilisationLive } = useScreenDataMode('reports-utilization');
  const [utilSummary, setUtilSummary] = useState<UtilisationSummary | null>(null);
  const [utilEmployeeMetrics, setUtilEmployeeMetrics] = useState<UtilisationMetrics[]>([]);
  const [utilLoading, setUtilLoading] = useState(false);

  // Capacity Planning data - calculated from utilisation metrics (weekly basis)
  const [capacityData, setCapacityData] = useState<CapacitySummary | null>(null);

  // Project Allocation data - from utilisation report
  const [projectUtilData, setProjectUtilData] = useState<ProjectUtilisation[]>([]);

  // Utilisation Trend data - last 8 weeks from utilisation report
  const [utilisationTrends, setUtilisationTrends] = useState<UtilisationTrend[]>([]);

  // Phase 2: Interactive State Management
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [teamSortBy, setTeamSortBy] = useState<'name' | 'hours' | 'utilization'>('name');
  const [teamFilterStatus, setTeamFilterStatus] = useState<'all' | 'pending' | 'active'>('all');
  const [activityFilter, setActivityFilter] = useState<'all' | 'submitted' | 'approved' | 'overdue'>('all');
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [alerts, setAlerts] = useState([
    { id: '1', type: 'overdue', message: '3 timesheets overdue for approval', count: 3, dismissible: true },
    { id: '2', type: 'utilization', message: '2 team members below 60% utilization', count: 2, dismissible: true },
  ]);

  // Bulk Approve state
  const [showBulkApproveConfirm, setShowBulkApproveConfirm] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  // Phase 4: Optimization & UX Refinements
  const [isLoading, setIsLoading] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dashboardSettings, setDashboardSettings] = useState({
    showHeroSection: true,
    showMetrics: true,
    showAlerts: true,
    showInsights: true,
    showAnalytics: true,
    showQuickActions: true,
    showTeamDetails: true,
    showActivity: true,
  });

  // Phase 4: Load dashboard preferences from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem('managerDashboardSettings');
    if (savedSettings) {
      try {
        setDashboardSettings(JSON.parse(savedSettings));
      } catch (e) {
        console.error('Failed to load dashboard settings', e);
      }
    }
  }, []);

  // Load utilisation data from API when in live mode (for Key Metrics cards)
  // Uses same API endpoint and date range as utilisation report (last 30 days)
  // This ensures dashboard values match the utilisation report exactly
  useEffect(() => {
    if (!isUtilisationLive) {
      setUtilSummary(null);
      return;
    }

    const loadUtilisationData = async () => {
      try {
        setUtilLoading(true);
        const today = new Date();
        const dateFrom = subDays(today, 30);
        const dateTo = today;
        
        // Use getUtilisationReport() instead of getUtilisationSummary() to match
        // the exact same data source as the Utilisation Report screen
        const report = await utilisationApi.getUtilisationReport({
          dateFrom,
          dateTo,
          includeNonBillable: true,
          groupBy: 'employee',
        });
        setUtilSummary(report.summary);
        setUtilEmployeeMetrics(report.employeeMetrics || []);
        setProjectUtilData(report.projectUtilisation || []);
        setUtilisationTrends(report.trends || []);
      } catch (error) {
        console.error('Failed to load utilisation data for dashboard:', error);
        // On error, fall back to calculated values (utilSummary stays null)
      } finally {
        setUtilLoading(false);
      }
    };

    loadUtilisationData();
  }, [isUtilisationLive]);

  // Update alerts with live data when in live mode
  useEffect(() => {
    if (mode !== 'live') return;

    setAlerts(prevAlerts => prevAlerts.map(alert => {
      if (alert.type === 'overdue' && managerSummary) {
        const count = managerSummary.approvalsRequired.overdueCount;
        return {
          ...alert,
          count,
          message: `${count} timesheet${count === 1 ? '' : 's'} overdue for approval`,
        };
      }

      if (alert.type === 'utilization' && utilEmployeeMetrics.length > 0) {
        const count = utilEmployeeMetrics.filter(emp => emp.utilisationRate < 0.6).length;
        return {
          ...alert,
          count,
          message: `${count} team member${count === 1 ? '' : 's'} below 60% utilization`,
        };
      }

      return alert;
    }));
  }, [mode, managerSummary, utilEmployeeMetrics]);

  // Calculate capacity planning data from utilisation metrics (weekly basis)
  // This runs when utilisation data is loaded in live mode
  useEffect(() => {
    if (!isUtilisationLive || utilEmployeeMetrics.length === 0) {
      setCapacityData(null);
      return;
    }

    // Calculate capacity metrics from employee utilisation data
    // Assuming 40 hours/week standard capacity per employee
    const WEEKLY_CAPACITY_PER_EMPLOYEE = 40;
    const employeeCount = utilEmployeeMetrics.length;
    const totalCapacity = employeeCount * WEEKLY_CAPACITY_PER_EMPLOYEE;
    
    // Calculate total allocated hours (average weekly from the utilisation data)
    // The utilisation data is for 30 days, so divide by ~4.3 weeks to get weekly average
    const WEEKS_IN_PERIOD = 4.3;
    const totalAllocatedMonthly = utilEmployeeMetrics.reduce((sum, emp) => sum + emp.totalHours, 0);
    const totalAllocated = Math.round(totalAllocatedMonthly / WEEKS_IN_PERIOD);
    
    // Calculate available capacity
    const totalAvailable = Math.max(0, totalCapacity - totalAllocated);
    
    // Count employees by utilisation status
    // Over-allocated: > 100% utilisation (working more than capacity)
    // Under-utilised: < 60% utilisation
    // Optimal: 60-100% utilisation
    let overAllocatedCount = 0;
    let underUtilisedCount = 0;
    let optimalCount = 0;
    
    utilEmployeeMetrics.forEach(emp => {
      const rate = emp.utilisationRate;
      if (rate > 1.0) {
        overAllocatedCount++;
      } else if (rate < 0.6) {
        underUtilisedCount++;
      } else {
        optimalCount++;
      }
    });
    
    // Calculate average utilisation
    const averageUtilisation = utilEmployeeMetrics.reduce((sum, emp) => sum + emp.utilisationRate, 0) / employeeCount;
    
    setCapacityData({
      totalCapacity,
      totalAllocated,
      totalAvailable,
      overAllocatedCount,
      underUtilisedCount,
      optimalCount,
      averageUtilisation,
      recommendations: [], // Recommendations could be added later
    });
  }, [isUtilisationLive, utilEmployeeMetrics]);

  // Phase 4: Save dashboard preferences to localStorage
  const updateDashboardSettings = (key: keyof typeof dashboardSettings, value: boolean) => {
    const newSettings = { ...dashboardSettings, [key]: value };
    setDashboardSettings(newSettings);
    localStorage.setItem('managerDashboardSettings', JSON.stringify(newSettings));
  };

  // Phase 4: Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K: Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
        searchInput?.focus();
      }
      // Ctrl/Cmd + /: Toggle help
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setShowHelp(!showHelp);
      }
      // Ctrl/Cmd + R: Refresh data
      if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
        e.preventDefault();
        handleRefresh();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showHelp]);

  // Phase 4: Refresh data handler
  const handleRefresh = () => {
    setIsLoading(true);
    // Simulate data refresh
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  // Calculate team metrics - Approvals uses direct reports filter, Team This Week uses current week filter
  const teamMetrics = useMemo(() => {
    // Get direct reports for approvals
    const directReports = employees.filter(e => e.managerId === currentEmployee.id);
    const directReportIds = new Set(directReports.map(e => e.id));
    
    // Filter timesheets to only include direct reports
    const directReportTimesheets = timesheets.filter(ts => directReportIds.has(ts.employeeId));
    
    // Calculate pending approvals - same logic as Approvals screen (direct reports)
    const myPendingTimesheets = directReportTimesheets.filter(ts => ts.status === 'submitted');
    const myPendingCount = myPendingTimesheets.length;
    const orgWidePendingCount = timesheets.filter(ts => ts.status === 'submitted').length;
    
    // Calculate overdue approvals from direct reports' timesheets
    const overdueApprovalsCount = myPendingTimesheets.filter(ts => {
      const daysSinceSubmission = ts.submittedAt 
        ? Math.floor((Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return daysSinceSubmission > 3;
    }).length;

    // Calculate Team This Week - use same week calculation as ThisWeekCard
    // Week starts on Monday (weekStartsOn: 1), ends on Sunday (Monday + 6 days)
    const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    const currentWeekEnd = addDays(currentWeekStart, 6);
    
    // Filter timesheets for current week only - exclude draft and rejected
    // Only count submitted, approved, or locked timesheets
    const finalizedStatuses = ['submitted', 'approved', 'locked'];
    const currentWeekTimesheets = directReportTimesheets.filter(ts => {
      const tsWeekEnding = format(new Date(ts.weekEnding), 'yyyy-MM-dd');
      const currentWeekEndStr = format(currentWeekEnd, 'yyyy-MM-dd');
      const isCurrentWeek = tsWeekEnding === currentWeekEndStr;
      const isFinalizedStatus = finalizedStatuses.includes(ts.status);
      return isCurrentWeek && isFinalizedStatus;
    });
    
    const totalHours = currentWeekTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
      }, 0);
    }, 0);

    const billableHours = currentWeekTimesheets.reduce((sum, ts) => {
      return sum + ts.entries.reduce((entrySum, entry) => {
        const project = projects.find(p => p.id === entry.projectId);
        const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
        return entrySum + (project?.billable ? hours : 0);
      }, 0);
    }, 0);

    const utilizationRate = totalHours > 0 ? (billableHours / totalHours) * 100 : 0;
    
        // Team size shows all active employees
        const activeEmployees = employees.filter(e => !e.status || e.status === 'active');

    // Use API data for team performance when in live mode
    const teamPerformanceFromApi = mode === 'live' && managerSummary?.teamPerformance;
    
    return {
      pendingApprovals: myPendingCount,
      orgWidePendingCount,
      overdueApprovals: overdueApprovalsCount,
      totalHours: Math.round(totalHours),
      billableHours: Math.round(billableHours),
      utilizationRate: Math.round(utilizationRate),
      teamSize: activeEmployees.length,
      trendPercentage: 0,
      // Team Performance section - use API data in live mode, fallback to calculated values
      teamPerformanceUtilization: teamPerformanceFromApi 
        ? Math.round(Number(managerSummary.teamPerformance.utilizationRate))
        : Math.round(utilizationRate),
      teamPerformanceTarget: teamPerformanceFromApi 
        ? Math.round(Number(managerSummary.teamPerformance.utilizationTarget))
        : 80,
      teamPerformanceIsAboveTarget: teamPerformanceFromApi 
        ? managerSummary.teamPerformance.isAboveTarget
        : utilizationRate >= 80,
      teamPerformanceTrend: teamPerformanceFromApi 
        ? Math.round(Number(managerSummary.teamPerformance.trendPercentage))
        : 0,
      teamPerformanceTeamSize: teamPerformanceFromApi 
        ? managerSummary.teamPerformance.teamSize
        : activeEmployees.length,
    };
  }, [timesheets, employees, projects, currentEmployee, mode, managerSummary]);

  // Helper function to get utilization accent class
  const getUtilizationAccent = (utilization: number): string => {
    if (utilization >= 85) return 'accent-success';
    if (utilization >= 75) return 'accent-info';
    if (utilization >= 60) return 'accent-warning';
    return 'accent-destructive';
  };

  // Calculate team member stats - show all employees, not just first 5
  const teamMemberStats = useMemo(() => {
    return employees.map(employee => {
      const employeeTimesheets = timesheets.filter(ts => ts.employeeId === employee.id);
      const totalHours = employeeTimesheets
        .filter(ts => ts.status === 'approved')
        .reduce((sum, ts) => {
          return sum + ts.entries.reduce((entrySum, entry) => {
            return entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0);
          }, 0);
        }, 0);

      const pendingCount = employeeTimesheets.filter(ts => ts.status === 'submitted').length;
      
      // Calculate utilization based on approved hours vs expected hours (40h/week standard)
      const EXPECTED_WEEKLY_HOURS = 40;
      const weeksOfData = Math.max(1, new Set(employeeTimesheets.map(ts => ts.weekEnding.toString())).size);
      const avgWeeklyHours = totalHours / weeksOfData;
      const utilization = Math.min(100, Math.round((avgWeeklyHours / EXPECTED_WEEKLY_HOURS) * 100));

      return {
        employee,
        totalHours: Math.round(totalHours),
        pendingCount,
        utilization,
      };
    });
  }, [employees, timesheets]);

  // Filter and sort team member stats based on user selections
  const filteredTeamMemberStats = useMemo(() => {
    let stats = [...teamMemberStats];

    // Apply search filter
    if (teamSearchQuery.trim()) {
      const query = teamSearchQuery.toLowerCase().trim();
      stats = stats.filter(({ employee }) => 
        employee.name.toLowerCase().includes(query) ||
        employee.department.toLowerCase().includes(query) ||
        employee.email.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (teamFilterStatus === 'pending') {
      stats = stats.filter(({ pendingCount }) => pendingCount > 0);
    } else if (teamFilterStatus === 'active') {
      stats = stats.filter(({ employee }) => 
        !employee.status || employee.status.toLowerCase() === 'active'
      );
    }

    // Apply sorting
    switch (teamSortBy) {
      case 'name':
        stats.sort((a, b) => a.employee.name.localeCompare(b.employee.name));
        break;
      case 'hours':
        stats.sort((a, b) => b.totalHours - a.totalHours);
        break;
      case 'utilization':
        stats.sort((a, b) => b.utilization - a.utilization);
        break;
    }

    return stats;
  }, [teamMemberStats, teamSearchQuery, teamFilterStatus, teamSortBy]);

  // Calculate timesheets to approve for selected members
  const timesheetsToApprove = useMemo(() => {
    if (selectedMembers.size === 0) return [];
    return timesheets.filter(ts => 
      ts.status === 'submitted' && 
      selectedMembers.has(ts.employeeId)
    );
  }, [timesheets, selectedMembers]);

  // Handle bulk approve click - show confirmation dialog
  const handleBulkApproveClick = () => {
    if (timesheetsToApprove.length === 0) {
      toast.info('No pending timesheets for selected team members');
      return;
    }
    setShowBulkApproveConfirm(true);
  };

  // Execute bulk approve - actually approve the timesheets
  const executeBulkApprove = () => {
    setIsBulkApproving(true);
    setShowBulkApproveConfirm(false);

    const count = timesheetsToApprove.length;
    
    if (count > 10) {
      toast.loading(`Approving ${count} timesheets...`, { id: 'bulk-approve' });
    }

    // Approve each timesheet
    timesheetsToApprove.forEach(ts => {
      updateTimesheet({
        ...ts,
        status: 'approved' as const,
        approvedAt: new Date(),
        approverId: currentEmployee.id,
        approverName: currentEmployee.name
      });
    });

    // Clear selection and show success
    setSelectedMembers(new Set());
    setIsBulkApproving(false);
    
    if (count > 10) {
      toast.dismiss('bulk-approve');
    }
    toast.success(`Successfully approved ${count} timesheet${count !== 1 ? 's' : ''}`);
  };

  // Export helper function to trigger file download
  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export Team Summary as PDF report
  const exportTeamSummary = () => {
    const reportDate = format(new Date(), 'yyyy-MM-dd HH:mm');
    const weekEnd = format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), 6), 'yyyy-MM-dd');
    
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'a4',
    });

    const margin = 40;
    const lineHeight = 16;
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = margin;

    const addLine = (text: string, fontSize = 10, isBold = false) => {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', isBold ? 'bold' : 'normal');
      doc.text(text, margin, y);
      y += lineHeight;
    };

    const addBlankLine = () => {
      y += lineHeight / 2;
    };

    // Title
    addLine('TEAM SUMMARY REPORT', 18, true);
    addBlankLine();
    
    // Report info
    addLine(`Generated: ${reportDate}`);
    addLine(`Manager: ${currentEmployee.name}`);
    addLine(`Week Ending: ${weekEnd}`);
    addBlankLine();
    
    // Overview section
    addLine('OVERVIEW', 14, true);
    addLine(`Team Size: ${teamMetrics.teamSize}`);
    addLine(`Pending Approvals: ${teamMetrics.pendingApprovals}`);
    addLine(`Overdue Approvals: ${teamMetrics.overdueApprovals}`);
    addLine(`Total Hours (This Week): ${teamMetrics.totalHours}h`);
    addLine(`Billable Hours: ${teamMetrics.billableHours}h`);
    addLine(`Utilization Rate: ${teamMetrics.utilizationRate}%`);
    addBlankLine();
    
    // Team Performance section
    addLine('TEAM PERFORMANCE', 14, true);
    addLine(`Current Utilization: ${teamMetrics.teamPerformanceUtilization}%`);
    addLine(`Target: ${teamMetrics.teamPerformanceTarget}%`);
    addLine(`Status: ${teamMetrics.teamPerformanceIsAboveTarget ? 'Above Target' : 'Below Target'}`);
    addLine(`Trend: ${teamMetrics.teamPerformanceTrend >= 0 ? '+' : ''}${teamMetrics.teamPerformanceTrend}%`);
    addBlankLine();
    
    // Team Members section
    addLine('TEAM MEMBERS', 14, true);
    addBlankLine();
    
    teamMemberStats.forEach(({ employee, totalHours, pendingCount, utilization }) => {
      addLine(employee.name, 11, true);
      addLine(`  Department: ${employee.department}`);
      addLine(`  Total Hours: ${totalHours}h`);
      addLine(`  Utilization: ${utilization}%`);
      addLine(`  Pending Timesheets: ${pendingCount}`);
      addBlankLine();
    });

    doc.save(`team-summary-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('Team Summary PDF downloaded');
  };

  // Export Utilization Data as CSV
  const exportUtilizationData = () => {
    const headers = ['Employee Name', 'Department', 'Email', 'Total Hours', 'Utilization %', 'Pending Timesheets', 'Status'];
    const rows = teamMemberStats.map(({ employee, totalHours, pendingCount, utilization }) => [
      employee.name,
      employee.department,
      employee.email,
      totalHours.toString(),
      utilization.toString(),
      pendingCount.toString(),
      employee.status || 'active'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    downloadFile(csvContent, `utilization-data-${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
    toast.success('Utilization Data exported to CSV');
  };

  // Export Project Hours as CSV
  const exportProjectHours = () => {
    const projectHoursMap = new Map<string, { projectName: string; projectCode: string; clientName: string; totalHours: number; billable: boolean }>();
    
    timesheets.forEach(ts => {
      ts.entries.forEach(entry => {
        const project = projects.find(p => p.id === entry.projectId);
        if (project) {
          const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
          const existing = projectHoursMap.get(project.id);
          if (existing) {
            existing.totalHours += hours;
          } else {
            projectHoursMap.set(project.id, {
              projectName: project.name,
              projectCode: project.code,
              clientName: project.clientName,
              totalHours: hours,
              billable: project.billable
            });
          }
        }
      });
    });
    
    const headers = ['Project Code', 'Project Name', 'Client', 'Total Hours', 'Billable'];
    const rows = Array.from(projectHoursMap.values())
      .sort((a, b) => b.totalHours - a.totalHours)
      .map(p => [
        p.projectCode,
        p.projectName,
        p.clientName,
        p.totalHours.toFixed(1),
        p.billable ? 'Yes' : 'No'
      ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    downloadFile(csvContent, `project-hours-${format(new Date(), 'yyyy-MM-dd')}.csv`, 'text/csv');
    toast.success('Project Hours exported to CSV');
  };

  return (
    <div className="space-y-6">
      {/* Header with integrated persona toggle */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold">Manager Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Team oversight, approvals, and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Phase 4: Toolbar Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            title="Refresh data (Ctrl+R)"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHelp(!showHelp)}
            title="Keyboard shortcuts (Ctrl+/)"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" title="Dashboard settings">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm font-semibold">Dashboard Sections</div>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showHeroSection', !dashboardSettings.showHeroSection)}>
                {dashboardSettings.showHeroSection ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                Hero Section
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showMetrics', !dashboardSettings.showMetrics)}>
                {dashboardSettings.showMetrics ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                Key Metrics
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showAlerts', !dashboardSettings.showAlerts)}>
                {dashboardSettings.showAlerts ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                Alerts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showInsights', !dashboardSettings.showInsights)}>
                {dashboardSettings.showInsights ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                AI Insights
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showAnalytics', !dashboardSettings.showAnalytics)}>
                {dashboardSettings.showAnalytics ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                Analytics
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => updateDashboardSettings('showTeamDetails', !dashboardSettings.showTeamDetails)}>
                {dashboardSettings.showTeamDetails ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
                Team Details
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <PersonaBadgeToggle />
        </div>
      </div>

      {/* Phase 4: Help Modal */}
      {showHelp && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-600" />
                Keyboard Shortcuts
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowHelp(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Focus search</span>
                <kbd className="px-2 py-1 text-xs font-semibold bg-white border rounded">Ctrl+K</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Toggle help</span>
                <kbd className="px-2 py-1 text-xs font-semibold bg-white border rounded">Ctrl+/</kbd>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Refresh data</span>
                <kbd className="px-2 py-1 text-xs font-semibold bg-white border rounded">Ctrl+R</kbd>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Phase 4: Loading Overlay */}
      {isLoading && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              <span className="text-sm font-medium">Refreshing dashboard data...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========================================
          TIER 1: HERO SECTION - Most Important
          ======================================== */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Team This Week - Lagoon Accent */}
        <Card className="hover:shadow-lg transition-shadow accent-lagoon border-l-4 border-l-[var(--accent-border)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Team This Week</CardTitle>
              <div className="p-1.5 rounded-md accent-lagoon bg-[var(--accent-bg-outline)]">
                <Users className="h-4 w-4 text-[var(--accent-text-on-outline)]" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-3xl font-bold text-[var(--accent-text)]">
                {teamMetrics.totalHours}h
              </div>
              <p className="text-sm text-muted-foreground mt-1">Total hours logged</p>
            </div>
            <div className={`flex items-center gap-2 text-sm ${teamMetrics.trendPercentage >= 0 ? 'accent-success' : 'accent-orange'}`}>
              {teamMetrics.trendPercentage >= 0 ? (
                <TrendingUp className="h-4 w-4 text-[var(--accent-text)]" />
              ) : (
                <TrendingDown className="h-4 w-4 text-[var(--accent-text)]" />
              )}
              <span className="text-[var(--accent-text)] font-medium">
                {teamMetrics.trendPercentage >= 0 ? '+' : ''}{teamMetrics.trendPercentage}%
              </span>
              <span className="text-muted-foreground">from last week</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Billable</span>
                <span className="font-semibold">{teamMetrics.billableHours}h ({teamMetrics.utilizationRate}%)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approvals Required - Warning Accent */}
        <Card className="hover:shadow-lg transition-shadow accent-warning border-l-4 border-l-[var(--accent-border)] cursor-pointer"
          onClick={() => onNavigate?.('/approvals')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Approvals Required</CardTitle>
              <div className="p-1.5 rounded-md accent-warning bg-[var(--accent-bg-outline)]">
                <CheckSquare className="h-4 w-4 text-[var(--accent-text-on-outline)]" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-3xl font-bold text-[var(--accent-text)]">
                {teamMetrics.pendingApprovals}
              </div>
              <p className="text-sm text-muted-foreground mt-1">Timesheets pending review</p>
            </div>
            {teamMetrics.overdueApprovals > 0 ? (
              <div className="flex items-center gap-2 text-sm accent-orange">
                <AlertTriangle className="h-4 w-4 text-[var(--accent-text)]" />
                <span className="text-[var(--accent-text)] font-medium">{teamMetrics.overdueApprovals} overdue</span>
                <span className="text-muted-foreground">(3+ days)</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm accent-success">
                <CheckSquare className="h-4 w-4 text-[var(--accent-text)]" />
                <span className="text-[var(--accent-text)] font-medium">All up to date</span>
              </div>
            )}
            <div className="pt-2 border-t accent-warning">
              <button className="text-sm font-medium text-[var(--accent-text)] hover:underline">
                Review Now →
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Team Performance - Purple Accent */}
        <Card className="hover:shadow-lg transition-shadow accent-purple border-l-4 border-l-[var(--accent-border)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Team Performance</CardTitle>
              <div className="p-1.5 rounded-md accent-purple bg-[var(--accent-bg-outline)]">
                <Target className="h-4 w-4 text-[var(--accent-text-on-outline)]" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-3xl font-bold text-[var(--accent-text)]">
                {teamMetrics.teamPerformanceUtilization}%
              </div>
              <p className="text-sm text-muted-foreground mt-1">Team utilization rate</p>
            </div>
            {teamMetrics.teamPerformanceIsAboveTarget ? (
              <div className="flex items-center gap-2 text-sm accent-success">
                <TrendingUp className="h-4 w-4 text-[var(--accent-text)]" />
                <span className="text-[var(--accent-text)] font-medium">Above target</span>
                <span className="text-muted-foreground">({teamMetrics.teamPerformanceTarget}%)</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm accent-orange">
                <TrendingDown className="h-4 w-4 text-[var(--accent-text)]" />
                <span className="text-[var(--accent-text)] font-medium">Below target</span>
                <span className="text-muted-foreground">({teamMetrics.teamPerformanceTarget}%)</span>
              </div>
            )}
            {teamMetrics.teamPerformanceTrend !== 0 && (
              <div className={`flex items-center gap-1 text-xs ${teamMetrics.teamPerformanceTrend > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {teamMetrics.teamPerformanceTrend > 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                <span>{teamMetrics.teamPerformanceTrend > 0 ? '+' : ''}{teamMetrics.teamPerformanceTrend}% vs last 30 days</span>
              </div>
            )}
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Team size</span>
                <span className="font-semibold">{teamMetrics.teamPerformanceTeamSize} members</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========================================
          TIER 2: KEY METRICS - Quick Stats
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Key Metrics
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card 
          className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-orange-500"
          onClick={() => onNavigate?.('/approvals')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
            <div className="p-2 rounded-lg bg-orange-100">
              <CheckSquare className="h-4 w-4 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{teamMetrics.pendingApprovals}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {teamMetrics.overdueApprovals > 0 && (
                <span className="text-orange-600 font-medium">
                  {teamMetrics.overdueApprovals} overdue
                </span>
              )}
              {teamMetrics.overdueApprovals === 0 && (
                <span className="text-green-600">All up to date</span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-blue-500"
          onClick={() => onNavigate?.('/team/members')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Team Size</CardTitle>
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{teamMetrics.teamSize}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Active team members
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-green-500"
          onClick={() => onNavigate?.('/reports/utilization')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Hours</CardTitle>
            <div className="p-2 rounded-lg bg-green-100">
              <Clock className="h-4 w-4 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {isUtilisationLive && utilSummary 
                ? utilSummary.totalHours.toFixed(1) 
                : teamMetrics.totalHours}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {isUtilisationLive && utilSummary 
                ? 'Last 30 days' 
                : 'This week'}
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-purple-500"
          onClick={() => onNavigate?.('/reports/utilization')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilization Rate</CardTitle>
            <div className="p-2 rounded-lg bg-purple-100">
              <Target className="h-4 w-4 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {isUtilisationLive && utilSummary 
                ? (utilSummary.averageUtilisation * 100).toFixed(1) 
                : teamMetrics.utilizationRate}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {(() => {
                const rate = isUtilisationLive && utilSummary 
                  ? utilSummary.averageUtilisation * 100 
                  : teamMetrics.utilizationRate;
                return rate >= 80 ? (
                  <span className="text-green-600">Above target (80%)</span>
                ) : (
                  <span className="text-orange-600">Below target (80%)</span>
                );
              })()}
            </p>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* ========================================
          PHASE 2: ALERTS & NOTIFICATIONS PANEL
          ======================================== */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-border" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Alerts & Notifications
            </h2>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="grid gap-3">
            {alerts.map((alert) => (
              <Card key={alert.id} className="border-l-4 border-l-orange-500 bg-orange-50/50">
                <CardContent className="py-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 rounded-lg bg-orange-100">
                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-orange-900">{alert.message}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 text-xs"
                            onClick={() => onNavigate?.('/approvals')}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Review
                          </Button>
                          {alert.dismissible && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 text-xs text-muted-foreground"
                              onClick={() => setAlerts(alerts.filter(a => a.id !== alert.id))}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Dismiss
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-200">
                      {alert.count}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ========================================
          PHASE 3: INTELLIGENCE & ANALYTICS
          ======================================== */}
      
      {/* AI-Powered Team Insights */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            AI-Powered Insights
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Predictive Analytics Card */}
          <Card className="border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50/50 to-transparent">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="h-5 w-5 text-purple-500" />
                  Predictive Analytics
                </CardTitle>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                  <Sparkles className="h-3 w-3 mr-1" />
                  AI
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
                  <TrendingUp className="h-4 w-4 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">Positive Trend Detected</p>
                    <p className="text-xs text-green-700 mt-1">
                      Team utilization expected to reach 85% by next week based on current trajectory
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <Activity className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">Resource Optimization</p>
                    <p className="text-xs text-blue-700 mt-1">
                      3 team members have capacity for additional projects (avg 8h/week available)
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-orange-900">Potential Bottleneck</p>
                    <p className="text-xs text-orange-700 mt-1">
                      Sarah Johnson at 95% capacity - consider redistributing workload
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Planning Widget */}
          <Card className="border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50/50 to-transparent">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Capacity Planning
                </CardTitle>
                <Button size="sm" variant="outline" className="h-7 text-xs">
                  <Download className="h-3 w-3 mr-1" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {utilLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading capacity data...</span>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div className="accent-success">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Available Capacity</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.totalAvailable}h/week` : '120h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData ? (capacityData.totalAvailable / capacityData.totalCapacity) * 100 : 30} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                    
                    <div className="accent-info">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Allocated Hours</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.totalAllocated}h/week` : '280h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData ? (capacityData.totalAllocated / capacityData.totalCapacity) * 100 : 70} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                    
                    <div className="accent-orange">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Overallocated</span>
                        <span className="font-semibold text-[var(--accent-text)]">
                          {capacityData ? `${capacityData.overAllocatedCount} employees` : '0h/week'}
                        </span>
                      </div>
                      <Progress 
                        value={capacityData && capacityData.totalCapacity > 0 
                          ? (capacityData.overAllocatedCount / utilEmployeeMetrics.length) * 100 
                          : 0} 
                        className="h-2" 
                        indicatorClassName="bg-[var(--accent-bg-solid)]" 
                      />
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Team Capacity</span>
                      <span className="font-bold">
                        {capacityData ? `${capacityData.totalCapacity}h/week` : '400h/week'}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Historical Trends & Project Allocation */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics & Trends
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Utilization Trend Chart */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Utilization Trend</CardTitle>
                  <CardDescription>Last 8 weeks performance</CardDescription>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onNavigate?.('/reports/utilization')}>
                  <FileText className="h-3 w-3 mr-1" />
                  Details
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { week: 'Week 1', value: utilisationTrends.length >= 1 ? Math.round(utilisationTrends.slice(-8)[0]?.utilisationRate * 100) : 72, color: 'bg-orange-500' },
                  { week: 'Week 2', value: utilisationTrends.length >= 2 ? Math.round(utilisationTrends.slice(-8)[1]?.utilisationRate * 100) : 75, color: 'bg-blue-500' },
                  { week: 'Week 3', value: utilisationTrends.length >= 3 ? Math.round(utilisationTrends.slice(-8)[2]?.utilisationRate * 100) : 78, color: 'bg-blue-500' },
                  { week: 'Week 4', value: utilisationTrends.length >= 4 ? Math.round(utilisationTrends.slice(-8)[3]?.utilisationRate * 100) : 82, color: 'bg-green-500' },
                  { week: 'Week 5', value: utilisationTrends.length >= 5 ? Math.round(utilisationTrends.slice(-8)[4]?.utilisationRate * 100) : 85, color: 'bg-green-500' },
                  { week: 'Week 6', value: utilisationTrends.length >= 6 ? Math.round(utilisationTrends.slice(-8)[5]?.utilisationRate * 100) : 83, color: 'bg-green-500' },
                  { week: 'Week 7', value: utilisationTrends.length >= 7 ? Math.round(utilisationTrends.slice(-8)[6]?.utilisationRate * 100) : 81, color: 'bg-green-500' },
                  { week: 'Week 8', value: utilisationTrends.length >= 8 ? Math.round(utilisationTrends.slice(-8)[7]?.utilisationRate * 100) : 84, color: 'bg-green-500' },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-16">{item.week}</span>
                    <div className="flex-1 bg-muted rounded-full h-6 overflow-hidden">
                      <div 
                        className={`h-full ${item.color} flex items-center justify-end pr-2 transition-all duration-500`}
                        style={{ width: `${item.value}%` }}
                      >
                        <span className="text-xs font-medium text-white">{item.value}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Average</span>
                <span className="font-semibold text-green-600">80% ↑ 8%</span>
              </div>
            </CardContent>
          </Card>

          {/* Project Allocation Breakdown */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Project Allocation</CardTitle>
                  <CardDescription>Current team distribution</CardDescription>
                </div>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onNavigate?.('/projects/active')}>
                  <PieChart className="h-3 w-3 mr-1" />
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {utilLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading project data...</span>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {(() => {
                      const PROJECT_COLORS = [
                        { color: 'bg-[hsl(var(--lagoon))]', textColor: 'text-[hsl(var(--lagoon))]' },
                        { color: 'bg-purple-500', textColor: 'text-purple-500' },
                        { color: 'bg-blue-500', textColor: 'text-blue-500' },
                        { color: 'bg-orange-500', textColor: 'text-orange-500' },
                        { color: 'bg-green-500', textColor: 'text-green-500' },
                        { color: 'bg-pink-500', textColor: 'text-pink-500' },
                      ];
                      
                      const demoData = [
                        { project: 'Website Redesign', hours: 120, percentage: 43 },
                        { project: 'Mobile App Dev', hours: 80, percentage: 29 },
                        { project: 'API Integration', hours: 50, percentage: 18 },
                        { project: 'Internal Tools', hours: 30, percentage: 10 },
                      ];
                      
                      // Use live data if available, otherwise use demo data
                      const projectData = projectUtilData.length > 0
                        ? projectUtilData
                            .sort((a, b) => b.totalHours - a.totalHours)
                            .slice(0, 4)
                            .map(p => {
                              const totalProjectHours = projectUtilData.reduce((sum, proj) => sum + proj.totalHours, 0);
                              return {
                                project: p.projectName,
                                hours: Math.round(p.totalHours),
                                percentage: totalProjectHours > 0 ? Math.round((p.totalHours / totalProjectHours) * 100) : 0,
                              };
                            })
                        : demoData;
                      
                      return projectData.map((item, index) => {
                        const colorSet = PROJECT_COLORS[index % PROJECT_COLORS.length];
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`h-3 w-3 rounded-full ${colorSet.color}`} />
                                <span className="text-sm font-medium">{item.project}</span>
                              </div>
                              <div className="text-right">
                                <span className={`text-sm font-semibold ${colorSet.textColor}`}>{item.hours}h</span>
                                <span className="text-xs text-muted-foreground ml-1">({item.percentage}%)</span>
                              </div>
                            </div>
                            <Progress value={item.percentage} className={`h-2 [&>div]:${colorSet.color}`} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Total Allocated</span>
                    <span className="font-bold">
                      {projectUtilData.length > 0 
                        ? `${Math.round(projectUtilData.reduce((sum, p) => sum + p.totalHours, 0))}h` 
                        : '280h/week'}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Export and Reporting */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reports & Export
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generate Reports</CardTitle>
            <CardDescription>Export team data and analytics for external use</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportTeamSummary}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Team Summary</span>
                  </div>
                  <span className="text-xs text-muted-foreground">PDF Report</span>
                </div>
              </Button>
              
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportUtilizationData}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Utilization Data</span>
                  </div>
                  <span className="text-xs text-muted-foreground">CSV Export</span>
                </div>
              </Button>
              
              <Button variant="outline" className="justify-start h-auto py-3" onClick={exportProjectHours}>
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Project Hours</span>
                  </div>
                  <span className="text-xs text-muted-foreground">CSV Export</span>
                </div>
              </Button>
              
              <Button variant="outline" className="justify-start h-auto py-3">
                <div className="flex flex-col items-start gap-1 w-full">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    <span className="font-medium">Custom Report</span>
                  </div>
                  <span className="text-xs text-muted-foreground">Configure</span>
                </div>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========================================
          TIER 3: QUICK ACTIONS - Manager Tools
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Quick Actions
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/approvals')}
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-primary" />
              Review Approvals
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {teamMetrics.pendingApprovals} timesheets waiting for your review
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/team/members')}
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Team Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              View team member performance and schedules
            </p>
          </CardContent>
        </Card>

        <Card 
          className="hover:shadow-lg transition-shadow cursor-pointer"
          onClick={() => onNavigate?.('/reports/utilization')}
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Utilization Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Analyze team utilization and productivity trends
            </p>
          </CardContent>
        </Card>
      </div>
      </div>

      {/* ========================================
          TIER 4: TEAM DETAILS - Individual Performance
          ======================================== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-border" />
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Team Details
          </h2>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Team Performance</CardTitle>
              <CardDescription>Individual team member metrics and status</CardDescription>
            </div>
            {selectedMembers.size > 0 && (
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{selectedMembers.size} selected</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedMembers(new Set())}>
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
                <Button 
                  size="sm" 
                  disabled={isBulkApproving}
                  onClick={handleBulkApproveClick}
                >
                  {isBulkApproving ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                  )}
                  Bulk Approve {timesheetsToApprove.length > 0 && `(${timesheetsToApprove.length})`}
                </Button>
              </div>
            )}
          </div>
          
          {/* Phase 2: Filtering and Sorting Controls */}
          <div className="flex flex-wrap gap-3 mt-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search team members..."
                value={teamSearchQuery}
                onChange={(e) => setTeamSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={teamFilterStatus} onValueChange={(value: any) => setTeamFilterStatus(value)}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                <SelectItem value="pending">Has Pending</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={teamSortBy} onValueChange={(value: any) => setTeamSortBy(value)}>
              <SelectTrigger className="w-[150px]">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Sort by Name</SelectItem>
                <SelectItem value="hours">Sort by Hours</SelectItem>
                <SelectItem value="utilization">Sort by Utilization</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
            {filteredTeamMemberStats.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No team members match your filters</p>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => {
                    setTeamSearchQuery('');
                    setTeamFilterStatus('all');
                  }}
                >
                  Clear filters
                </Button>
              </div>
            ) : null}
            {filteredTeamMemberStats.map(({ employee, totalHours, pendingCount, utilization }) => (
              <Card key={employee.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <input
                          type="checkbox"
                          checked={selectedMembers.has(employee.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedMembers);
                            if (e.target.checked) {
                              newSelected.add(employee.id);
                            } else {
                              newSelected.delete(employee.id);
                            }
                            setSelectedMembers(newSelected);
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-primary">
                            {employee.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{employee.name}</p>
                          <p className="text-xs text-muted-foreground">{employee.department}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-medium">{totalHours}h</p>
                          <p className="text-xs text-muted-foreground">Total hours</p>
                        </div>
                        {pendingCount > 0 && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            {pendingCount} pending
                          </Badge>
                        )}
                        
                        {/* Phase 2: Quick Action Menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => onNavigate?.(`/team/${employee.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onNavigate?.(`/approvals?employee=${employee.id}`)}>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Review Timesheets
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => onNavigate?.(`/messages?to=${employee.id}`)}>
                              <MessageSquare className="h-4 w-4 mr-2" />
                              Send Message
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    <div className={`space-y-1 ${getUtilizationAccent(utilization)}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Utilization</span>
                        <span className="font-medium text-[var(--accent-text)]">
                          {utilization}%
                        </span>
                      </div>
                      <Progress 
                        value={utilization} 
                        className="h-2"
                        indicatorClassName="bg-[var(--accent-bg-solid)]"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Team Activity - Phase 2: With Filtering */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle>Recent Team Activity</CardTitle>
              <CardDescription>Latest submissions and approvals across your team</CardDescription>
            </div>
            <Select value={activityFilter} onValueChange={(value: any) => setActivityFilter(value)}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Activity</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(() => {
              // Helper function to format relative time
              const getRelativeTime = (date: Date | undefined) => {
                if (!date) return 'Unknown';
                const now = new Date();
                const diffMs = now.getTime() - new Date(date).getTime();
                const diffMins = Math.floor(diffMs / 60000);
                const diffHours = Math.floor(diffMs / 3600000);
                const diffDays = Math.floor(diffMs / 86400000);
                
                if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
                if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
                return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
              };

              // Helper function to calculate total hours from timesheet entries
              const getTotalHours = (entries: { hours: { monday: number; tuesday: number; wednesday: number; thursday: number; friday: number; saturday: number; sunday: number } }[]) => {
                return entries.reduce((total, entry) => {
                  const h = entry.hours;
                  return total + h.monday + h.tuesday + h.wednesday + h.thursday + h.friday + h.saturday + h.sunday;
                }, 0);
              };

              // Demo data for fallback
              const demoActivities = [
                {
                  employee: 'Sarah Johnson',
                  action: 'Submitted timesheet',
                  description: 'Week ending 22/12/2024',
                  time: '30 minutes ago',
                  icon: Clock,
                  color: 'text-blue-600',
                  bgColor: 'bg-blue-50',
                  type: 'submitted' as const,
                },
                {
                  employee: 'Michael Chen',
                  action: 'Timesheet approved',
                  description: 'Week ending 15/12/2024 - 40 hours',
                  time: '2 hours ago',
                  icon: CheckSquare,
                  color: 'text-green-600',
                  bgColor: 'bg-green-50',
                  type: 'approved' as const,
                },
                {
                  employee: 'Emma Wilson',
                  action: 'Submitted timesheet',
                  description: 'Week ending 22/12/2024',
                  time: '3 hours ago',
                  icon: Clock,
                  color: 'text-blue-600',
                  bgColor: 'bg-blue-50',
                  type: 'submitted' as const,
                },
                {
                  employee: 'David Brown',
                  action: 'Overdue submission',
                  description: 'Week ending 15/12/2024 - 3 days overdue',
                  time: '3 days ago',
                  icon: AlertTriangle,
                  color: 'text-orange-600',
                  bgColor: 'bg-orange-50',
                  type: 'overdue' as const,
                },
              ];

              // Build activities from real timesheet data if available
              const activities = timesheets.length > 0
                ? timesheets
                    .filter(ts => ts.status === 'submitted' || ts.status === 'approved' || ts.status === 'draft')
                    .map(ts => {
                      const weekEndingDate = new Date(ts.weekEnding);
                      const weekEndingStr = format(weekEndingDate, 'dd/MM/yyyy');
                      const totalHours = getTotalHours(ts.entries);
                      const now = new Date();
                      const daysSinceWeekEnding = Math.floor((now.getTime() - weekEndingDate.getTime()) / 86400000);
                      
                      // Determine activity type based on status and timing
                      if (ts.status === 'approved') {
                        return {
                          employee: ts.employeeName,
                          action: 'Timesheet approved',
                          description: `Week ending ${weekEndingStr} - ${totalHours} hours`,
                          time: getRelativeTime(ts.approvedAt),
                          icon: CheckSquare,
                          color: 'text-green-600',
                          bgColor: 'bg-green-50',
                          type: 'approved' as const,
                          sortDate: ts.approvedAt ? new Date(ts.approvedAt) : weekEndingDate,
                        };
                      } else if (ts.status === 'submitted') {
                        return {
                          employee: ts.employeeName,
                          employeeId: ts.employeeId,
                          action: 'Submitted timesheet',
                          description: `Week ending ${weekEndingStr}`,
                          time: getRelativeTime(ts.submittedAt),
                          icon: Clock,
                          color: 'text-blue-600',
                          bgColor: 'bg-blue-50',
                          type: 'submitted' as const,
                          sortDate: ts.submittedAt ? new Date(ts.submittedAt) : weekEndingDate,
                        };
                      } else if (ts.status === 'draft' && daysSinceWeekEnding > 7) {
                        // Draft timesheets that are more than 7 days past week ending are overdue
                        return {
                          employee: ts.employeeName,
                          action: 'Overdue submission',
                          description: `Week ending ${weekEndingStr} - ${daysSinceWeekEnding - 7} days overdue`,
                          time: `${daysSinceWeekEnding} days ago`,
                          icon: AlertTriangle,
                          color: 'text-orange-600',
                          bgColor: 'bg-orange-50',
                          type: 'overdue' as const,
                          sortDate: weekEndingDate,
                        };
                      }
                      return null;
                    })
                    .filter((activity): activity is NonNullable<typeof activity> => activity !== null)
                    .sort((a, b) => b.sortDate.getTime() - a.sortDate.getTime())
                    .slice(0, 10) // Show up to 10 recent activities
                : demoActivities;

              return activities
                .filter(item => activityFilter === 'all' || item.type === activityFilter)
                .map((item, index) => (
                  <div key={index} className="flex items-start gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className={`p-2 rounded-lg ${item.bgColor}`}>
                      <item.icon className={`h-4 w-4 ${item.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.employee}</p>
                      <p className="text-sm text-muted-foreground">{item.action}</p>
                      <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="text-xs text-muted-foreground">{item.time}</p>
                      {item.type === 'submitted' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs"
                          onClick={() => onNavigate?.('/approvals')}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                ));
            })()}
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Bulk Approve Confirmation Dialog */}
      <AlertDialog open={showBulkApproveConfirm} onOpenChange={setShowBulkApproveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve {timesheetsToApprove.length} timesheet{timesheetsToApprove.length !== 1 ? 's' : ''} for {selectedMembers.size} team member{selectedMembers.size !== 1 ? 's' : ''}.
              This will mark them as approved with you ({currentEmployee.name}) recorded as the approver.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeBulkApprove}>
              Approve {timesheetsToApprove.length} Timesheet{timesheetsToApprove.length !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
